import React from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBIcon,
  MDBSpinner,
  MDBBadge,
} from "mdb-react-ui-kit";

import { doFetch, doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";

/**
 * BatchAssignmentsModal — Asignar / revocar vendedores sobre un lote.
 *
 * Props:
 *  - show: boolean
 *  - batch: { idBatch, name, ... }
 *  - onClose: () => void
 *
 * Endpoints:
 *  - GET    /api/teansari/getBatchAssignments?idBatch=
 *  - POST   /api/teansari/assignBatchToSeller   body: { idBatch, idUsuario }
 *  - DELETE /api/teansari/revokeBatchAssignment/{id}
 *  - GET    /api/teansari/getTeansariUsers  (filtra a los que tienen TICKET_SELL/TICKET_MANAGE)
 *
 * Requiere TEAN.TICKET_MANAGE.
 */
class BatchAssignmentsModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      assignments: [],
      candidates: [],
      selectedUserId: "",
      saving: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.show !== this.props.show && this.props.show) {
      this.loadAll();
    }
  }

  handleClose = () => {
    if (this.props.onClose) this.props.onClose();
  };

  loadAll = () => {
    const { batch } = this.props;
    if (!batch || batch.idBatch == null) return;
    this.setState({ loading: true });

    Promise.all([
      doFetchJson("TEAN", "getBatchAssignments?idBatch=" + batch.idBatch),
      doFetchJson("TEAN", "getTeansariUsers"),
    ])
      .then(([assignRes, usersRes]) => {
        const assignments =
          assignRes && assignRes.STATE === "SUCCESS" && Array.isArray(assignRes.DATA)
            ? assignRes.DATA
            : [];
        const users =
          usersRes && usersRes.STATE === "SUCCESS" &&
          usersRes.DATA && Array.isArray(usersRes.DATA.users)
            ? usersRes.DATA.users
            : [];

        // Sólo usuarios con TICKET_SELL o TICKET_MANAGE en TEAN.
        const eligible = users.filter((u) => {
          const perms = u.permisos || u.appPermisos || [];
          const set = new Set(
            perms.map((p) => (typeof p === "string" ? p : p && p.nombre)).filter(Boolean)
          );
          return set.has("TICKET_SELL") || set.has("TICKET_MANAGE");
        });

        // Excluye los que ya están asignados (activos).
        const assignedIds = new Set(assignments.map((a) => a.idUsuario));
        const candidates = eligible.filter((u) => !assignedIds.has(u.idUsuario || u.IDUsuario));

        this.setState({ assignments, candidates, loading: false, selectedUserId: "" });
      })
      .catch(() => {
        toast.show({ message: "Error cargando asignaciones", type: "danger" });
        this.setState({ loading: false, assignments: [], candidates: [] });
      });
  };

  handleSelectChange = (e) => {
    this.setState({ selectedUserId: e.target.value });
  };

  handleAssign = () => {
    const { batch } = this.props;
    const { selectedUserId } = this.state;
    if (!selectedUserId || !batch) return;
    this.setState({ saving: true });

    doFetch("TEAN", "assignBatchToSeller", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        idBatch: batch.idBatch,
        idUsuario: parseInt(selectedUserId, 10),
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({ message: "Vendedor asignado", type: "success" });
          this.loadAll();
        } else {
          toast.show({
            message: (json && json.MESSAGE) || "No se pudo asignar el vendedor",
            type: "danger",
          });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }))
      .finally(() => this.setState({ saving: false }));
  };

  handleRevoke = (assignment) => {
    if (!assignment || !assignment.idAssignment) return;
    if (!window.confirm("¿Revocar la asignación de " + (assignment.userName || "este vendedor") + "?")) return;
    doFetchJson("TEAN", "revokeBatchAssignment/" + assignment.idAssignment, { method: "DELETE" })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({ message: "Asignación revocada", type: "success" });
          this.loadAll();
        } else {
          toast.show({
            message: (json && json.MESSAGE) || "No se pudo revocar la asignación",
            type: "danger",
          });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }));
  };

  candidateLabel = (u) => {
    const apodo = u.apodo || u.Apodo;
    const nombre = u.nombre || u.Nombre;
    const apellido = u.apellido || u.Apellido;
    const email = u.email || u.Email;
    const base = apodo || [nombre, apellido].filter(Boolean).join(" ") || email || "Usuario " + (u.idUsuario || u.IDUsuario);
    return base + (email && email !== apodo ? " (" + email + ")" : "");
  };

  render() {
    const { show, batch } = this.props;
    const { loading, assignments, candidates, selectedUserId, saving } = this.state;
    if (!batch) return null;

    return (
      <MDBModal show={show} onHide={this.handleClose} tabIndex="-1">
        <MDBModalDialog centered scrollable size="lg">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                <MDBIcon fas icon="user-tag" className="me-2" />
                Vendedores asignados — {batch.name}
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={this.handleClose} />
            </MDBModalHeader>
            <MDBModalBody>
              {loading ? (
                <div className="text-center py-4">
                  <MDBSpinner />
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <label className="form-label small text-muted">Asignar nuevo vendedor</label>
                    <div className="d-flex">
                      <select
                        className="form-select form-select-sm me-2"
                        value={selectedUserId}
                        onChange={this.handleSelectChange}
                        disabled={candidates.length === 0 || saving}
                      >
                        <option value="">
                          {candidates.length === 0
                            ? "No hay vendedores elegibles disponibles"
                            : "Selecciona un vendedor…"}
                        </option>
                        {candidates.map((u) => (
                          <option key={u.idUsuario || u.IDUsuario} value={u.idUsuario || u.IDUsuario}>
                            {this.candidateLabel(u)}
                          </option>
                        ))}
                      </select>
                      <MDBBtn
                        color="primary"
                        size="sm"
                        onClick={this.handleAssign}
                        disabled={!selectedUserId || saving}
                      >
                        <MDBIcon fas icon="plus" className="me-1" />
                        Asignar
                      </MDBBtn>
                    </div>
                    <div className="text-muted small mt-1">
                      Sólo aparecen usuarios con el permiso TICKET_SELL o TICKET_MANAGE en TEAN.
                    </div>
                  </div>

                  <hr />

                  <h6 className="mb-2">
                    <MDBIcon fas icon="users" className="me-1" />
                    Vendedores con acceso a este lote
                    <MDBBadge color="light" className="ms-2 text-dark">
                      {assignments.length}
                    </MDBBadge>
                  </h6>

                  {assignments.length === 0 ? (
                    <div className="text-muted fst-italic small">
                      Este lote aún no tiene vendedores asignados.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th>Vendedor</th>
                            <th>Login</th>
                            <th className="text-end">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignments.map((a) => (
                            <tr key={a.idAssignment}>
                              <td>{a.userName || ("Usuario " + a.idUsuario)}</td>
                              <td className="text-muted small">{a.userLogin || "—"}</td>
                              <td className="text-end">
                                <MDBBtn
                                  size="sm"
                                  color="light"
                                  onClick={() => this.handleRevoke(a)}
                                  title="Revocar"
                                >
                                  <MDBIcon fas icon="user-slash" className="text-danger" />
                                </MDBBtn>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" outline onClick={this.handleClose}>
                Cerrar
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }
}

export default BatchAssignmentsModal;
